/**
 * What the phone's screen-share stage is showing: the share, nothing yet, or
 * nothing any more.
 *
 * The stage read every "no frame on screen" as "stopped" and covered itself in
 * black under "Your screen sharing has stopped". A share that has only just
 * started has no frame on screen either: the viewer's consumer is waiting for
 * the presenter's first keyframe to come through the SFU. On a phone that took
 * long enough to be filed as a black screen in place of the shared content
 * (BUG-000750). So that wait is its own state, `loading`: a stream is attached,
 * playback has been asked for, its video track is live, and no frame has been
 * decoded yet.
 *
 * `stopped` keeps the old reading for everything else — no stream, playback
 * paused, or a video track that has ended.
 */

export type PresentedVideoState = 'live' | 'loading' | 'stopped'

/** The slice of an `HTMLVideoElement` this reads. */
export type PresentedVideoSource = {
  srcObject: unknown
  paused: boolean
  readyState: number
}

/** `HTMLMediaElement.HAVE_CURRENT_DATA`: a frame is ready to be drawn. */
const HAVE_CURRENT_DATA = 2

export function presentedVideoState(el: PresentedVideoSource): PresentedVideoState {
  const stream = el.srcObject as { getVideoTracks?: () => { readyState: string }[] } | null
  if (!stream || el.paused) return 'stopped'
  if (el.readyState >= HAVE_CURRENT_DATA) return 'live'
  const tracks = stream.getVideoTracks?.() ?? []
  return tracks.some((t) => t.readyState === 'live') ? 'loading' : 'stopped'
}
